import React, { useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, MenuItem, InputLabel, Grid, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { selectCreateAuditTrailOpened, selectCreateEventError, selectCreatingNewEvent, selectCreateEventData, selectNewEvent } from '../state';
import events from '../routes/lab-requests-list/events';
import { red } from '@material-ui/core/colors';
import { format } from 'date-fns';
import { TextField, Select } from 'mui-rff';
import { Field, Form } from 'react-final-form';
import { FormApi } from 'final-form';

interface AuditTrailEvent {
  code: string;
  title: string;
};

const formatLabel = (label: string) => {
  const result = label.replace(/([A-Z])/g,' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const required = (value: any) => (value !== undefined && value !== '' ? undefined : 'Required');

export default function CreateAuditTrailEvent(
  {onClose, onSave}: {onClose: Function, onSave: Function,}
) {
  const createEventOpened = useSelector(selectCreateAuditTrailOpened);
  const eventData = useSelector(selectCreateEventData);
  const loading = useSelector(selectCreatingNewEvent);
  const error = useSelector(selectCreateEventError);
  const event = events.find(({code}) => code === (eventData ? eventData.code : ''));
  const newEventBeingCreated = useSelector(selectNewEvent);
  let formInstance: FormApi | null = null;

  useEffect(
    () => {
      if (formInstance && !createEventOpened) formInstance.reset();
    },
    [createEventOpened, formInstance]
  );

  if (!event) {
    return null;
  }

  const initialValues = newEventBeingCreated ? newEventBeingCreated : {
    timestamp: new Date().toISOString(),
    metadata: {}
  };

  const close = () => {
    onClose();
  };

  const onSubmit = (values: any) => {
    onSave(values);
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, form, values, invalid }) => {
        formInstance = form;
        return (
          <Dialog
            open={createEventOpened}
            onClose={close}
            aria-labelledby="create-audit-trail-event">
            <form onSubmit={handleSubmit} noValidate>
            
              <DialogTitle id="create-audit-trail-event">{"Create audit trail event"}</DialogTitle>
              <DialogContent style={{minWidth: 500}}>
                {error && (<div style={{color: red.A400, marginBottom: 28}}>
                  {error}
                </div>)}
                <Grid container
                  direction="column"
                  justify="flex-start"
                  alignItems="center"
                  spacing={2}>
                  <Grid item style={{width: '100%'}}>
                    <TextField
                      id="audit-event-timestamp"
                      label="Event date"
                      type="datetime-local"
                      name="timestamp"
                      fieldProps={{
                        validate: required,
                        format: (value) => value ? format(new Date(value), "yyyy-MM-dd'T'hh:mm") : value,
                        parse: (value) => value ? new Date(value).toISOString() : value
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {
                    Object.keys(event.metadata).length > 0 ? <Grid item>
                      <Typography variant="subtitle1" component="h4">
                        Metadata
                      </Typography>
                    </Grid> : null
                  }
                  {
                    Object.keys(event.metadata)
                      .map((key) => {
                        const {type, values} = (event.metadata as any)[key];
                        if (type === 'text' || type === 'number' || type === 'datetime-local') {
                          return (
                            <Grid key={key} item style={{width: '100%'}}>
                              <TextField
                                required
                                type={type}
                                label={formatLabel(key)}
                                id={key}
                                fieldProps={{
                                  validate: required,
                                  format: (value) => (type === 'datetime-local' && value) ? format(new Date(value), "yyyy-MM-dd'T'hh:mm") : value,
                                  parse: (value) => (type === 'datetime-local' && value) ? new Date(value).toISOString() : value
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                name={`metadata.${key}`}
                              />
                            </Grid>
                          );
                        } else if (type === 'select') {
                          return (
                            <Grid item key={key} style={{width: '100%'}}>
                              <InputLabel id={`${key}-label`}>{formatLabel(key)}</InputLabel>
                              <Field name={`metadata.${key}`} validate={required}>
                                {(props) => (
                                  <Select
                                    required
                                    name={props.input.name}
                                    labelId={`${key}-label`}
                                    id={key}
                                  >
                                    {
                                      values.map((value: string) =>
                                        <MenuItem key={value.toString()} value={value}>{value.toString()}</MenuItem>
                                      )
                                    }
                                  </Select>
                                )}
                              </Field>
                            </Grid>
                          )
                        } else {
                          return null;
                        }
                      })
                  }
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="secondary"
                  autoFocus disabled={invalid || loading}>
                  {loading && <CircularProgress style={{marginRight: 8}} size={24} />}
                  Save
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        )
      }}
    />
  )
}